import {
    Box,
    Collapse,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import type { IEmployeeCoverage } from '@sharedInterfaces/IOpportunity';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CertificateCompare from '@src/App/Components/Items/Compare/CertificateCompare/CertificateCompare';
import RoleCompare from '@src/App/Components/Items/Compare/RoleCompare/RoleCompare';
import SkillsLevelCompare from '@src/App/Components/Items/Compare/SkillsLevelCompare/SkillsLevelCompare';
import type { AppState } from '@store/store';
import { Award, Calendar, ChevronDown, ChevronUp, TrendingUp, User } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { EmployeePicture } from '../../EmployeePicture/EmployeePicture';

interface EmployeeSuggestionProps {
    hideAvailability?: true;
    hideName?: true;
    suggestedEmployee: IEmployeeCoverage;
    inDialog?: true;
    expended?: true;
    rightTopButton?: (id: number) => JSX.Element;
    handleDragStart?: (event: React.DragEvent<HTMLDivElement>, item: IEmployeeCoverage | null) => void;
    handleDragEnd?: (event: React.DragEvent<HTMLDivElement>) => void;
}

/**
 * Generates the employee coverage.
 *
 * @name EmployeeCoverage
 * @param {EmployeeSuggestionProps} params
 *        The parameters for generating the employee coverage.
 * @returns {JSX.Element} The generated employee coverage.
 */
export function EmployeeCoverage({
    rightTopButton,
    hideAvailability,
    hideName,
    suggestedEmployee,
    inDialog,
    expended,
    handleDragStart,
    handleDragEnd,
}: EmployeeSuggestionProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).sales;
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    const [isExpanded, setExpanded] = useState(expended ? true : false);

    const emp = suggestedEmployee;
    const thisAllEmployee = allEmployees.find((e) => e.id === emp.id);
    const availabilityObj = thisAllEmployee?.availability;
    const availability =
        !availabilityObj || (availabilityObj.availability === 0 && availabilityObj.workDayCount === 0)
            ? null
            : Math.round(availabilityObj.availability);

    const availabilityText = availability ? `${availability}%` : 'N/A';
    const nameSplit = thisAllEmployee ? thisAllEmployee.title.split(' ') : [];
    const firstName = nameSplit.length > 0 ? nameSplit[0] : '';
    const lastName = nameSplit.length > 1 ? nameSplit[nameSplit.length - 1] : '';

    const certScore =
        emp.coveredCertificates.length + emp.missingCertificates.length > 0
            ? Math.round(
                  (emp.coveredCertificates.length / (emp.coveredCertificates.length + emp.missingCertificates.length)) *
                      100,
              )
            : 0;

    const roleScore =
        emp.coveredRoles && emp.missingRoles && emp.coveredRoles.length + emp.missingRoles.length > 0
            ? Math.round((emp.coveredRoles.length / (emp.coveredRoles.length + emp.missingRoles.length)) * 100)
            : null;

    // Coverage-based background color
    const getCoverageColor = () => {
        if (emp.score > 0.8) return alpha(theme.palette.success.main, 0.05);
        if (emp.score > 0.5) return alpha(theme.palette.warning.main, 0.05);
        return alpha(theme.palette.error.main, 0.05);
    };

    const StatCard = ({
        icon,
        label,
        value,
        color,
    }: {
        icon: React.ReactNode;
        label: string;
        value: string;
        color: string;
    }) => (
        <Box
            sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                bgcolor: alpha(color, 0.1),
                border: `1px solid ${alpha(color, 0.2)}`,
                minWidth: { xs: 'auto', sm: 120 },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {React.cloneElement(icon as React.ReactElement, { size: 14, color })}
                <Typography variant="caption" sx={{ fontWeight: 600, color }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
        </Box>
    );

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: getCoverageColor(),
                position: 'relative',
                overflow: 'hidden',
            }}
            draggable={handleDragStart ? true : false}
            onDragStart={(event) => {
                handleDragStart && handleDragStart(event, emp);
            }}
            onDragEnd={handleDragEnd}
        >
            {/* Progress indicator */}
            <LinearProgress
                variant="determinate"
                value={emp.score * 100}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    bgcolor: alpha(theme.palette.divider, 0.1),
                    '& .MuiLinearProgress-bar': {
                        bgcolor:
                            emp.score > 0.8
                                ? theme.palette.success.main
                                : emp.score > 0.5
                                  ? theme.palette.warning.main
                                  : theme.palette.error.main,
                    },
                }}
            />

            <Box sx={{ p: { lg: 3, md: 2, xs: 2 }, pt: { lg: 4, md: 3, xs: 3 } }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    {!hideName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            {thisAllEmployee && (
                                <EmployeePicture
                                    avatarSize={isMobile ? 40 : 45}
                                    openDialogOnClick={true}
                                    employee={{
                                        firstName: firstName,
                                        lastName: lastName,
                                        pictureId: thisAllEmployee.pictureId,
                                        id: thisAllEmployee.id,
                                    }}
                                />
                            )}
                            <Box>
                                <NavLink
                                    to={entityTypeToLink(emp.id, EEntityType.EMPLOYEE)}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Typography
                                        variant={isMobile ? 'subtitle1' : 'h6'}
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {emp.title}
                                    </Typography>
                                </NavLink>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(!isExpanded)}
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                },
                            }}
                        >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </IconButton>
                        {rightTopButton && <Box sx={{ ml: 1 }}>{rightTopButton(emp.id)}</Box>}
                    </Box>
                </Box>

                {/* Stats Grid */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(auto-fit, minmax(120px, 1fr))' },
                        gap: 1.5,
                        mb: 2,
                    }}
                >
                    {(emp.coveredSkills.length > 0 || emp.missingSkills.length > 0) && (
                        <StatCard
                            icon={<TrendingUp />}
                            label={langStrings.skillScore}
                            value={`${Math.round(emp.score * 100)}%`}
                            color={theme.palette.primary.main}
                        />
                    )}
                    {(emp.coveredSkills.length > 0 || emp.missingSkills.length > 0) && (
                        <StatCard
                            icon={<TrendingUp />}
                            label={langStrings.skillCoverage}
                            value={`${Math.round(emp.skillCoverage * 100)}%`}
                            color={theme.palette.info.main}
                        />
                    )}
                    {roleScore !== null && (
                        <StatCard
                            icon={<User />}
                            label={langStrings.roleScore}
                            value={`${roleScore}%`}
                            color={theme.palette.secondary.main}
                        />
                    )}
                    {(emp.coveredCertificates.length > 0 || emp.missingCertificates.length > 0) && (
                        <StatCard
                            icon={<Award />}
                            label={langStrings.certificateScore}
                            value={`${certScore}%`}
                            color={theme.palette.warning.main}
                        />
                    )}
                    {!hideAvailability && (
                        <StatCard
                            icon={<Calendar />}
                            label={langStrings.availability}
                            value={availabilityText}
                            color={
                                availability && availability > 80
                                    ? theme.palette.success.main
                                    : theme.palette.error.main
                            }
                        />
                    )}
                </Box>

                {/* Expandable Content */}
                <Collapse in={isExpanded}>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        {emp.coveredRoles && emp.coveredRoles.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}
                                >
                                    {langStrings.coveredRoles}
                                </Typography>
                                <RoleCompare size="medium" inDialog={inDialog} roles={emp.coveredRoles} />
                            </Box>
                        )}
                        {emp.missingRoles && emp.missingRoles.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}
                                >
                                    {langStrings.missingRoles}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, 1fr)',
                                            sm: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        },
                                        gap: 1,
                                    }}
                                >
                                    <RoleCompare
                                        size="medium"
                                        negative={true}
                                        inDialog={inDialog}
                                        roles={emp.missingRoles}
                                    />
                                </Box>
                            </Box>
                        )}
                        {emp.coveredSkills.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}
                                >
                                    {langStrings.coveredSkills}
                                </Typography>
                                <SkillsLevelCompare
                                    employeeId={thisAllEmployee?.id}
                                    size="medium"
                                    inDialog={inDialog}
                                    skills={emp.coveredSkills}
                                    levelBadgeVariant="arrow"
                                    skillContext="covered"
                                />
                            </Box>
                        )}
                        {emp.missingSkills.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}
                                >
                                    {langStrings.missingSkills}
                                </Typography>
                                <SkillsLevelCompare
                                    employeeId={thisAllEmployee?.id}
                                    size="medium"
                                    inDialog={inDialog}
                                    skills={emp.missingSkills}
                                    levelBadgeVariant="arrow"
                                    skillContext="missing"
                                />
                            </Box>
                        )}
                        {emp.coveredCertificates.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}
                                >
                                    {langStrings.coveredCertificates}
                                </Typography>
                                <CertificateCompare
                                    size="medium"
                                    inDialog={inDialog}
                                    certificates={emp.coveredCertificates}
                                />
                            </Box>
                        )}
                        {emp.missingCertificates.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}
                                >
                                    {langStrings.missingCertificates}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, 1fr)',
                                            sm: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        },
                                        gap: 1,
                                    }}
                                >
                                    <CertificateCompare
                                        size="medium"
                                        negative={true}
                                        inDialog={inDialog}
                                        certificates={emp.missingCertificates}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Collapse>
            </Box>
        </Paper>
    );
}
